/**
 * Inicio de sesión con Google en la web de Hermes, mismo patrón que Argos IA
 * (web/chat.html): Google Identity Services, botón nativo renderButton() y
 * el credential (id_token) se manda a POST /auth/google. Sin Firebase: así
 * el aud del id_token es el cliente web del proyecto 986006015737 que ya
 * verifica el servidor (verify_google_id_token en server/api/index.php).
 */
export const GOOGLE_WEB_CLIENT_ID = "986006015737-l0ntlvbdq4p9k5es5gdf572ac8pm87g9.apps.googleusercontent.com";

type GoogleCredentialResponse = { credential?: string };

interface GoogleAccountsId {
  initialize(config: {
    client_id: string;
    ux_mode?: "popup" | "redirect";
    callback: (resp: GoogleCredentialResponse) => void;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      theme?: "outline" | "filled_black" | "filled_blue";
      size?: "large" | "medium" | "small";
      text?: "continue_with" | "signin_with" | "signup_with";
      shape?: "rectangular" | "pill";
      locale?: string;
      width?: number;
    },
  ): void;
  cancel(): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

const SCRIPT_SRC = "https://accounts.google.com/gsi/client";
let scriptPromise: Promise<void> | null = null;

function loadGoogleIdentityScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("Sin ventana de navegador"));
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("No se pudo cargar Google")));
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("No se pudo cargar Google. Revisa tu conexión."));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Carga el script de Google (si hace falta) y dibuja el botón "Continuar con Google"
 * dentro de `host`. Llama a `onCredential` con el id_token cuando el usuario elige cuenta,
 * o a `onError` con un mensaje en español si algo falla. No lanza si el usuario simplemente
 * cierra el selector sin elegir cuenta (Google no llama al callback en ese caso).
 */
export async function mountGoogleButton(
  host: HTMLElement,
  onCredential: (credential: string) => void,
  onError: (message: string) => void,
  theme: "outline" | "filled_black" = "outline",
): Promise<void> {
  try {
    await loadGoogleIdentityScript();
  } catch (err) {
    onError(err instanceof Error ? err.message : "No se pudo cargar Google.");
    return;
  }
  const id = window.google?.accounts?.id;
  if (!id) {
    onError("Google no está disponible en este navegador.");
    return;
  }
  try {
    id.initialize({
      client_id: GOOGLE_WEB_CLIENT_ID,
      ux_mode: "popup",
      callback: (resp) => {
        if (resp?.credential) onCredential(resp.credential);
        else onError("Google no ha devuelto credencial. Inténtalo de nuevo.");
      },
    });
    host.innerHTML = "";
    id.renderButton(host, {
      theme,
      size: "large",
      text: "continue_with",
      shape: "pill",
      locale: "es",
      width: Math.min(360, host.clientWidth || 300),
    });
  } catch {
    onError("No se pudo mostrar el botón de Google. Entra con tu correo.");
  }
}
